import * as THREE from "three";

/**
 * Utility class for handling text box styles and geometry in a 3D scene.
 * @category Components
 * @subcategory TextBox
 */
export default class TextBoxMethods {
  /**
   * Initializes a new instance of the TextBoxMethods class.
   */
  constructor() {}

  /**
   * Applies styles to a text box DOM element based on user data and scale.
   * @param {HTMLElement} textBox - The DOM element representing the text box.
   * @param {Object} userData - Data containing styles and properties for the text box.
   * @param {number} scale - The scale factor for dimensions.
   */
  applyStyles(textBox, userData, scale) {
    textBox.innerHTML = this.convertPlainTextToHtml(userData.text);
    textBox.style.width = `${userData.w * scale}px`;
    textBox.style.height = `${userData.h * scale}px`;
    textBox.style.fontSize = `${userData.fontSize}px`;
    textBox.style.color = this.convertColorToCss(userData.textColor);
    textBox.style.backgroundColor =
      this.convertColorToCss(userData.bgColor) || "rgb(255,255,255)";
    textBox.style.fontWeight = userData.bold ? "bold" : "normal";
    textBox.style.fontFamily = userData.fontFamily || "Arial";
    textBox.style.border = userData.outlineColor
      ? `1px solid ${this.convertColorToCss(userData.outlineColor)}`
      : "none";
  }

  cleanup() {
    console.log("✅ Cleaning up TextBoxMethods...");

    // Since this class does not hold any Three.js objects or event listeners,
    // the cleanup mainly involves nullifying the instance properties if needed.
    this.applyStyles = null;
    this.createTextBoxMesh = null;
    this.convertPlainTextToHtml = null;
    this.convertColorToCss = null;

    console.log("✅ TextBoxMethods cleanup complete.");
}

  /**
   * Creates a Three.js plane mesh to represent a text box in 3D space.
   * @param {number} w - The width of the plane.
   * @param {number} h - The height of the plane.
   * @returns {THREE.Mesh} A Three.js plane mesh with the specified dimensions.
   */
  createTextBoxMesh(w, h) {
    const planeGeometry = new THREE.PlaneGeometry(w, h);
    const material = new THREE.MeshBasicMaterial({
      visible: false,
    });
    const planeMesh = new THREE.Mesh(planeGeometry, material);
    planeMesh.rotation.x = -Math.PI / 2;
    planeMesh.name = "textBoxMesh";
    return planeMesh;
  }

  /**
   * Converts plain text to HTML by wrapping each line in a `<div>` element.
   * Empty lines are filtered out.
   * @param {string} text - The plain text to convert.
   * @returns {string} The converted HTML string.
   */
  convertPlainTextToHtml(text) {
    // Split text into lines, trim each line, and filter out empty lines
    const cleanedText = text
      .split("\n")
      .map((line) => line.trim()) // Remove leading/trailing whitespace
      .filter((line) => line !== ""); // Remove empty lines

    // Convert remaining lines to HTML
    return cleanedText.map((line) => `<div>${line}</div>`).join("");
  }

  /**
   * Converts a color object to a CSS RGB string.
   * @param {Object} color - An object representing a color with `red`, `green`, and `blue` properties.
   * @param {number} color.red - The red component (0-255).
   * @param {number} color.green - The green component (0-255).
   * @param {number} color.blue - The blue component (0-255).
   * @returns {string} The CSS RGB string, or an empty string if the color is invalid.
   */
  convertColorToCss(color) {
    return color ? `rgb(${color.red}, ${color.green}, ${color.blue})` : "";
  }
}
